import * as amplitude from "@amplitude/analytics-browser";
import axios from "axios";
import qs from "query-string";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useBankFeeds } from "../../components/bankFeeds/bankFeedsAuthProvider";
import { BankFeedsSupportedRutterPlatforms } from "../../components/bankFeeds/constants";
import { DataSharing } from "../../components/bankFeeds/dataSharing";
import { DefaultLogin } from "../../components/bankFeeds/defaultLogin";
import BankFeedsCommonLayout, {
  LOCAL_STORAGE_ORG_INFO,
  OrgInfo,
} from "../../components/bankFeeds/layout";
import { SecureConnection } from "../../components/bankFeeds/secureConnection";
import { getBackendUrl } from "../../utils";

const BankFeedsAuth: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState<string>("");

  return (
    <BankFeedsCommonLayout errorMessage={errorMessage}>
      <BankFeedsAuthComponent setErrorMessage={setErrorMessage} />
    </BankFeedsCommonLayout>
  );
};

export const BankFeedsAuthComponent = ({
  setErrorMessage,
}: {
  setErrorMessage: (value: string) => void;
}) => {
  const history = useHistory();
  const { search } = history.location;
  const parsedSearch: any = qs.parse(search);
  const { login_challenge } = parsedSearch;

  const {
    setOrgInfo,
    setShowDefaultLogin,
    setIsSubmitting,
    setIsFetchingOrgInfo,
    setIsRedirecting,
    setShowInstitutionMessage,
    orgInfo,
    password,
    isRedirecting,
    isFetchingOrgInfo,
    showDefaultLogin,
  } = useBankFeeds();

  useEffect(() => {
    axios
      .get(
        `${getBackendUrl()}/bank_feeds_connector/auth/org?login_challenge=${login_challenge}`,
      )
      .then((response) => {
        if (typeof response.data !== "object") {
          setOrgInfo({ orgId: response.data });
        } else {
          const {
            orgId,
            companyLogoUrl,
            otpInstructions,
            name,
            redirectUrl,
            termsAndConditions,
            platform,
          } = response.data;
          const orgInfo: OrgInfo = {
            orgId,
            companyLogoUrl,
            otpInstructions,
            name,
            redirectUrl,
            termsAndConditions,
            platform,
          };
          setOrgInfo(orgInfo);
          localStorage.setItem(LOCAL_STORAGE_ORG_INFO, JSON.stringify(orgInfo));
          amplitude.track("Bank Feeds: Auth Page Loaded", {
            orgId,
          });

          setShowDefaultLogin(!redirectUrl);
        }
      })
      .catch((err) => {
        amplitude.track("Bank Feeds: Org Fetch Failed");
        history.push("/bank_feeds_error");
      })
      .finally(() => {
        setIsFetchingOrgInfo(false);
      });
  }, [
    login_challenge,
    setOrgInfo,
    setIsFetchingOrgInfo,
    setShowDefaultLogin,
    setErrorMessage,
    history,
  ]);

  const handleCompleteSubmit = async () => {
    let pathname = "/bank_feeds_connector/auth/login";
    switch (orgInfo.platform) {
      case BankFeedsSupportedRutterPlatforms.INTUIT_BANK_FEEDS:
        pathname = "/intuit_bank_feeds_connector/auth/login";
        break;
      case BankFeedsSupportedRutterPlatforms.PLAID_BANK_FEEDS:
        pathname = "/plaid_bank_feeds_connector/auth/login";
        break;
      case BankFeedsSupportedRutterPlatforms.YODLEE_BANK_FEEDS:
        pathname = "/yodlee_bank_feeds_connector/auth/login";
        break;
    }

    amplitude.track("Bank Feeds: Auth Submit Clicked");
    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${getBackendUrl()}${pathname}?challenge=${login_challenge}&otp=${password}&orgId=${
          orgInfo.orgId
        }`,
      );
      amplitude.track("Bank Feeds: Auth OTP Success, redirecting to Ory");

      window.location.replace(response.data);
    } catch (e: any) {
      amplitude.track("Bank Feeds: Auth OTP Error");
      setErrorMessage(
        e.response?.data?.error_message ?? "Something went wrong.",
      );
      setIsSubmitting(false);
    }
  };

  const handleCompleteCancel = () => {
    amplitude.track("Bank Feeds: Auth Cancel Clicked");
    history.push({ pathname: "/bank_feeds_cancelled", state: { orgInfo } });
  };

  const continueToLoginHandler = () => {
    if (!orgInfo.redirectUrl) return setShowDefaultLogin(true);

    setIsRedirecting(true);
    setShowInstitutionMessage(true);
    window.location.replace(orgInfo.redirectUrl);
  };

  return isRedirecting || isFetchingOrgInfo ? (
    <SecureConnection />
  ) : showDefaultLogin ? (
    <DefaultLogin
      handleCompleteCancel={handleCompleteCancel}
      handleCompleteSubmit={handleCompleteSubmit}
      platform={orgInfo.platform as BankFeedsSupportedRutterPlatforms}
    />
  ) : (
    <DataSharing
      cancelHandler={handleCompleteCancel}
      continueToLoginHandler={continueToLoginHandler}
    />
  );
};
export default BankFeedsAuth;
